import { Box, VStack, Text, useColorModeValue, Icon, Link as ChakraLink, Divider, Image, Flex, Spinner, Collapse, IconButton } from '@chakra-ui/react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { useEffect, useRef, useCallback, useState, useMemo } from 'react';
import { useAdminNavScrollRetention } from '../../hooks/useAdminNavScrollRetention';
import { 
  FaTachometerAlt, 
  FaUsers, 
  FaFutbol, 
  FaCalendarAlt, 
  FaNewspaper, 
  FaHandshake, 
  FaImage, 
  FaEnvelope, 
  FaCog, 
  FaPalette,
  FaHome,
  FaSignOutAlt,
  FaPaperPlane,
  FaAward,
  FaSyncAlt,
  FaBook,
  FaMobileAlt,
  FaChartBar,
  FaFolder,
  FaAddressBook,
  FaBars,
  FaPoll,
  FaPaintBrush,
  FaVideo,
  FaCamera,
  FaTshirt,
  FaBullhorn,
  FaUserShield,
  FaFileAlt,
  FaLink,
  FaComments,
  FaGift,
  FaQrcode,
  FaTools,
  FaDollarSign,
  FaFileInvoice,
  FaReceipt,
  FaUserFriends,
  FaMoneyBillWave,
  FaCogs,
  FaDatabase,
  FaRocket,
  FaFileInvoiceDollar,
  FaTimes
} from 'react-icons/fa';
import { ChevronDownIcon, ChevronRightIcon } from '@chakra-ui/icons';
import { useAuth } from '../../contexts/AuthContext';
import { useQuery } from '@tanstack/react-query';
import { getUpcomingEvents } from '../../services/eventService';
import { getAllNavigationItems, NavigationItem, seedDefaultNavigation, getEditorAllowedAdminNav } from '../../services/navigation';
import { usePublicSettings } from '../../hooks/usePublicSettings';
import { assetUrl } from '../../utils/url';

interface NavItemProps {
  icon: any;
  to?: string;
  children: React.ReactNode;
  onClick?: (e?: React.MouseEvent) => void;
}

const NavItem = ({ icon, to, children, onClick }: NavItemProps) => {
  const location = useLocation();
  // Use exact matching for navigation items to prevent multiple active states
  const isActive = to ? location.pathname === to : false;
  const activeBg = useColorModeValue('blue.50', 'blue.900');
  const activeColor = useColorModeValue('blue.600', 'blue.300');
  const hoverBg = useColorModeValue('gray.100', 'gray.700');
  
  const handleClick = (e: React.MouseEvent) => {
    // Call the onClick handler first
    if (onClick) {
      onClick(e);
      // If onClick called preventDefault, respect it
      if (e.isDefaultPrevented()) {
        return;
      }
    }
    
    // For external links or non-router navigation, allow default behavior
    // For RouterLink, React Router will handle the navigation
  };
  
  // If onClick is provided without `to`, render as a button-like link
  const LinkComponent = to ? RouterLink : 'a';
  const linkProps = to ? { to } : { href: '#' };
  
  return (
    <ChakraLink
      as={LinkComponent}
      {...linkProps}
      display="flex"
      alignItems="center"
      px={3}
      py={2.5}
      borderRadius="lg"
      bg={isActive ? activeBg : 'transparent'}
      color={isActive ? activeColor : 'inherit'}
      fontWeight={isActive ? 'semibold' : 'medium'}
      fontSize="sm"
      _hover={{
        textDecoration: 'none',
        bg: isActive ? activeBg : hoverBg,
        transform: 'translateX(2px)',
      }}
      transition="all 0.2s ease"
      onClick={handleClick}
      data-navitem="true"
      data-active={isActive ? 'true' : undefined}
      position="relative"
      _before={isActive ? {
        content: '""',
        position: 'absolute',
        left: 0,
        top: '50%',
        transform: 'translateY(-50%)',
        width: '3px',
        height: '60%',
        bg: activeColor,
        borderRadius: 'full',
      } : {}}
    >
      <Icon as={icon} mr={3} boxSize={4} />
      <Text flex={1}>{children}</Text>
    </ChakraLink>
  );
};

interface AdminSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  bg?: string;
  borderRight?: string;
  borderColor?: string;
}

// Icon mapping for navigation items
const getIconForPageType = (pageType?: string): any => {
  const iconMap: Record<string, any> = {
    dashboard: FaTachometerAlt,
    analytics: FaChartBar,
    teams: FaUsers,
    matches: FaCalendarAlt,
    activities: FaCalendarAlt,
    players: FaFutbol,
    articles: FaNewspaper,
    categories: FaFileAlt,
    about: FaBook,
    videos: FaVideo,
    gallery: FaImage,
    scoreboard: FaTachometerAlt,
    scoreboard_remote: FaMobileAlt,
    clothing: FaTshirt,
    sponsors: FaHandshake,
    banners: FaBullhorn,
    messages: FaEnvelope,
    contacts: FaAddressBook,
    newsletter: FaPaperPlane,
    polls: FaPoll,
    navigation: FaBars,
    competition_aliases: FaAward,
    prefetch: FaRocket, // Better icon for prefetch & cache
    users: FaUserShield,
    settings: FaPalette,
    files: FaFolder,
    media: FaFolder,
    docs: FaBook,
    shortlinks: FaLink,
    comments: FaComments,
    engagement: FaAward,
    sweepstakes: FaGift,
    'manual-data': FaTools,
    'financial-dashboard': FaMoneyBillWave, // Better finance icon
    'qr-codes': FaQrcode,
    'invoices': FaFileInvoiceDollar, // Enhanced invoice icon
    'invoice-settings': FaFileInvoiceDollar, // Enhanced invoice settings icon
    'customers': FaUserFriends, // Better customers icon
    'expenses': FaMoneyBillWave, // Better expenses icon
    'manual_facr': FaTools, // For manual FACR
  };
  return iconMap[pageType || ''] || FaFileAlt;
};

const AdminSidebar = ({ 
  isOpen, 
  onClose, 
  bg: bgProp,
  borderRight = '1px',
  borderColor: borderColorProp
}: AdminSidebarProps) => {
  const { logout, user } = useAuth();
  const { data: publicSettings } = usePublicSettings();
  const isAdmin = (user as any)?.role === 'admin';
  const defaultBg = useColorModeValue('white', '#1a1d29');
  const defaultBorderColor = useColorModeValue('gray.200', 'rgba(255, 255, 255, 0.12)');
  const textColor = useColorModeValue('gray.800', '#e2e8f0');
  const bg = bgProp || defaultBg;
  const borderColor = borderColorProp || defaultBorderColor;
  // Check if e-shop is enabled (from backend config)
  const isEshopEnabled = (publicSettings as any)?.eshop_enabled || false;
  // Check if club data mode is manual (to show manual data tab)
  const isManualClubDataMode = (publicSettings?.club_data_mode || '').toLowerCase() === 'manual';
  // Hoisted color tokens to keep hook calls stable across renders
  const dividerColor = useColorModeValue('gray.200', 'whiteAlpha.300');
  const categoryTextColor = useColorModeValue('gray.600', 'gray.300');
  const categoryIconColor = useColorModeValue('gray.500', 'gray.400');
  const headerMutedColor = useColorModeValue('gray.500', 'gray.400');
  const scrollThumb = useColorModeValue('gray.300', 'gray.600');
  const scrollThumbHover = useColorModeValue('gray.400', 'gray.500');
  const badgeBgGreen = useColorModeValue('green.100', 'green.900');
  const badgeColorGreen = useColorModeValue('green.700', 'green.200');
  const badgeBorderGreen = useColorModeValue('green.200', 'green.700');
  const badgeBgGray = useColorModeValue('gray.100', 'whiteAlpha.200');
  const badgeColorGray = useColorModeValue('gray.700', 'gray.300');
  const badgeBorderGray = useColorModeValue('gray.200', 'whiteAlpha.300');
  // Upcoming events count for badge
  const { data: upcomingEvents } = useQuery({ queryKey: ['admin-sidebar-upcoming-events'], queryFn: getUpcomingEvents });
  const upcomingCount = Array.isArray(upcomingEvents) ? upcomingEvents.length : 0;
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const seedFixRef = useRef<{ about: boolean }>({ about: false });
  const location = useLocation();
  // Use the updated scroll retention hook
  const { scrollToCurrentPage, isReady, debug } = useAdminNavScrollRetention({
    scrollContainerSelector: '[data-sidebar="true"]',
    enableDebug: process.env.NODE_ENV === 'development'
  });

  // Helper function to render NavItem
  const renderNavItem = useCallback((props: NavItemProps) => {
    return <NavItem {...props} />;
  }, []);
  
  // Dynamic navigation state
  const [navItems, setNavItems] = useState<NavigationItem[]>([]);
  const [navLoading, setNavLoading] = useState(true);
  // presence checks consider children as well
  const hasItemDeep = useCallback((predicate: (it: NavigationItem) => boolean): boolean => {
    const check = (it: NavigationItem): boolean => {
      if (predicate(it)) return true;
      if (Array.isArray(it.children)) {
        return it.children.some(check);
      }
      return false;
    };
    return navItems.some(check);
  }, [navItems]);
  const hasShortlinks = useMemo(() => hasItemDeep(it => (it.page_type === 'shortlinks') || (it.url === '/admin/shortlinks')), [hasItemDeep]);
  const hasEngagement = useMemo(() => hasItemDeep(it => (it.page_type === 'engagement') || (it.url === '/admin/engagement')), [hasItemDeep]);
  const hasComments = useMemo(() => hasItemDeep(it => (it.page_type === 'comments') || (it.url === '/admin/komentare')), [hasItemDeep]);
  const hasSweepstakes = useMemo(() => hasItemDeep(it => (it.page_type === 'sweepstakes') || (it.url === '/admin/sweepstakes')), [hasItemDeep]);
  const hasCompetitionAliases = useMemo(() => hasItemDeep(it => (it.page_type === 'competition_aliases') || (it.url === '/admin/aliasy-soutezi')), [hasItemDeep]);
  const hasClothing = useMemo(() => hasItemDeep(it => (it.page_type === 'clothing') || (it.url === '/admin/obleceni')), [hasItemDeep]);
  const hasEshopProducts = useMemo(() => hasItemDeep(it => (it.page_type === 'eshop_products') || (it.url === '/admin/eshop-produkty')), [hasItemDeep]);
  const hasAbout = useMemo(() => hasItemDeep(it => (it.page_type === 'about') || (it.url === '/admin/o-klubu')), [hasItemDeep]);
  const hasVideos = useMemo(() => hasItemDeep(it => (it.page_type === 'videos') || (it.url === '/admin/videa')), [hasItemDeep]);
  const hasGallery = useMemo(() => hasItemDeep(it => (it.page_type === 'gallery') || (it.url === '/admin/galerie')), [hasItemDeep]);
  const hasScoreboard = useMemo(() => hasItemDeep(it => (it.page_type === 'scoreboard') || (it.url === '/admin/scoreboard')), [hasItemDeep]);
  const hasScoreboardRemote = useMemo(() => hasItemDeep(it => (it.page_type === 'scoreboard_remote') || (it.url === '/admin/scoreboard/remote')), [hasItemDeep]);
  const hasSponsors = useMemo(() => hasItemDeep(it => (it.page_type === 'sponsors') || (it.url === '/admin/sponzori')), [hasItemDeep]);
  const hasBanners = useMemo(() => hasItemDeep(it => (it.page_type === 'banners') || (it.url === '/admin/bannery')), [hasItemDeep]);
  const hasMessages = useMemo(() => hasItemDeep(it => (it.page_type === 'messages') || (it.url === '/admin/zpravy')), [hasItemDeep]);
  const hasContacts = useMemo(() => hasItemDeep(it => (it.page_type === 'contacts') || (it.url === '/admin/kontakty')), [hasItemDeep]);
  const hasNewsletter = useMemo(() => hasItemDeep(it => (it.page_type === 'newsletter') || (it.url === '/admin/newsletter')), [hasItemDeep]);
  const hasPolls = useMemo(() => hasItemDeep(it => (it.page_type === 'polls') || (it.url === '/admin/ankety')), [hasItemDeep]);
  const hasFiles = useMemo(() => hasItemDeep(it => (it.page_type === 'files') || (it.url === '/admin/soubory')), [hasItemDeep]);
  const hasNavigation = useMemo(() => hasItemDeep(it => (it.page_type === 'navigation') || (it.url === '/admin/navigace')), [hasItemDeep]);
  const hasUsers = useMemo(() => hasItemDeep(it => (it.page_type === 'users') || (it.url === '/admin/uzivatele')), [hasItemDeep]);
  const hasSettingsPage = useMemo(() => hasItemDeep(it => (it.page_type === 'settings') || (it.url === '/admin/nastaveni')), [hasItemDeep]);
  const hasAnalytics = useMemo(() => hasItemDeep(it => (it.page_type === 'analytics') || (it.url === '/admin/analytika')), [hasItemDeep]);
  const hasPrefetch = useMemo(() => hasItemDeep(it => (it.page_type === 'prefetch') || (it.url === '/admin/prefetch')), [hasItemDeep]);
  const hasManualData = useMemo(() => hasItemDeep(it => (it.page_type === 'manual_data') || (it.url === '/admin/manual-data')), [hasItemDeep]);
  

  // Collapsed state for admin categories (dropdown items)
  type CollapsedMap = Record<number, boolean>;
  const COLLAPSE_KEY = 'admin-sidebar-collapsed-v1';
  const [collapsed, setCollapsed] = useState<CollapsedMap>({});

  useEffect(() => {
    try {
      const raw = localStorage.getItem(COLLAPSE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as CollapsedMap;
        if (parsed && typeof parsed === 'object') setCollapsed(parsed);
      }
    } catch {}
  }, []);

  useEffect(() => {
    // Ensure keys exist for current dropdown categories
    setCollapsed(prev => {
      const next: CollapsedMap = { ...prev };
      navItems.forEach(it => {
        if (it.type === 'dropdown' && typeof it.id === 'number' && typeof next[it.id] === 'undefined') {
          next[it.id] = false; // default expanded
        }
      });
      return next;
    });
  }, [navItems]);

  const toggleCollapsed = useCallback((id?: number) => {
    if (!id) return;
    setCollapsed(prev => {
      const next = { ...prev, [id]: !prev[id] } as CollapsedMap;
      try { localStorage.setItem(COLLAPSE_KEY, JSON.stringify(next)); } catch {}
      return next;
    });
  }, []);

  // The scroll handling is now managed by the useAdminNavScrollRetention hook

  // Load dynamic navigation from API
  useEffect(() => {
    let active = true;
    // Editors: load editor-allowed admin navigation
    if (!isAdmin) {
      (async () => {
        try {
          setNavLoading(true);
          const editorItems = await getEditorAllowedAdminNav();
          if (active) {
            setNavItems(editorItems || []);
          }
        } catch (e) {
          if (active) setNavItems([]);
        } finally {
          if (active) setNavLoading(false);
        }
      })();
      return () => { active = false };
    }
    (async () => {
      try {
        const items = await getAllNavigationItems();
        if (active && Array.isArray(items)) {
          // Filter only admin navigation items
          const adminItems = items.filter(item => item.requires_admin);
          
          // Auto-seed if admin navigation is empty and user is admin
          if (adminItems.length === 0 && isAdmin) {
            try {
              console.log('Admin navigation empty, auto-seeding...');
              await seedDefaultNavigation();
              const newItems = await getAllNavigationItems();
              if (active && Array.isArray(newItems)) {
                const newAdminItems = newItems.filter(item => item.requires_admin);
                setNavItems(newAdminItems);
              }
            } catch (seedError) {
              console.error('Auto-seed failed:', seedError);
              // Continue with empty navigation (will show fallback)
              setNavItems(adminItems);
            }
          } else {
            // If admin navigation exists but specific required items are missing (e.g., 'about'),
            // trigger idempotent seed to backfill missing ones and reload once.
            const hasAboutItem = adminItems.some(it => {
              if (it.page_type === 'about') return true;
              if (Array.isArray(it.children)) {
                return it.children.some(c => c.page_type === 'about' || c.url === '/admin/o-klubu');
              }
              return false;
            });
            if (!hasAboutItem && isAdmin && !seedFixRef.current.about) {
              try {
                seedFixRef.current.about = true;
                await seedDefaultNavigation();
                const reloaded = await getAllNavigationItems();
                if (active && Array.isArray(reloaded)) {
                  const reloadedAdmin = reloaded.filter(item => item.requires_admin);
                  setNavItems(reloadedAdmin);
                }
              } catch (e) {
                console.warn('Seed backfill for about failed:', e);
                setNavItems(adminItems);
              }
            } else {
              setNavItems(adminItems);
            }
          }
        }
      } catch (error) {
        console.error('Failed to load admin navigation:', error);
      } finally {
        if (active) setNavLoading(false);
      }
    })();
    return () => { active = false };
  }, [isAdmin]);

  // Auto-scroll to current page when navigation loads
  useEffect(() => {
    if (isReady && !navLoading) {
      scrollToCurrentPage();
      debug('Auto-scroll to current page after navigation load');
    }
  }, [isReady, navLoading, scrollToCurrentPage, debug]);

  return (
    <Box
      as="nav"
      position="fixed"
      left={0}
      top={0}
      bottom={0}
      width={{ base: '320px', md: '260px' }}
      bg={bg}
      borderRightWidth={borderRight}
      borderColor={borderColor}
      pt={{ base: 16, md: 5 }}
      display={{ base: isOpen ? 'block' : 'none', md: 'block' }}
      zIndex={{ base: 11, md: 10 }}
      overflowY="auto"
      overflowX="hidden"
      boxShadow={{ base: 'lg', md: 'none' }}
      transform={{ base: isOpen ? 'translateX(0)' : 'translateX(-100%)', md: 'translateX(0)' }}
      transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
      ref={scrollRef}
      onScroll={undefined}
      data-sidebar="true"
      css={{
        '&::-webkit-scrollbar': { width: '4px' },
        '&::-webkit-scrollbar-track': { background: 'transparent' },
        '&::-webkit-scrollbar-thumb': { background: scrollThumb, borderRadius: '2px' },
        '&::-webkit-scrollbar-thumb:hover': { background: scrollThumbHover },
      }}
    >
      <VStack align="stretch" spacing={{ base: 2, md: 1 }} px={{ base: 6, md: 3 }} pb={6}>
        {/* Close button for mobile */}
        <Flex justify="flex-end" display={{ base: 'flex', md: 'none' }} w="100%" mb={2}>
          <IconButton
            aria-label="Zavřít menu"
            icon={<FaTimes />}
            variant="ghost"
            onClick={onClose}
            size="sm"
            borderRadius="full"
            _hover={{ bg: useColorModeValue('gray.100', 'gray.700') }}
          />
        </Flex>
        
        <Box px={{ base: 6, md: 3 }} mb={{ base: 6, md: 4 }}>
          <Flex align="center" gap={{ base: 4, md: 3 }} mb={2}>
            <Image 
              src={assetUrl(publicSettings?.club_logo_url) || publicSettings?.club_logo_url || '/dist/img/logo-club-empty.svg'} 
              alt="Club Logo" 
              boxSize={{ base: '56px', md: '48px' }}
              objectFit="contain"
              fallbackSrc="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'%3E%3Crect width='100' height='100' fill='%23e2e8f0'/%3E%3Ctext x='50' y='55' text-anchor='middle' font-size='40' fill='%23718096'%3EMC%3C/text%3E%3C/svg%3E"
              borderRadius="md"
            />
            <VStack align="start" spacing={0}>
              <Text 
                fontSize={{ base: '2xl', md: 'xl' }} 
                fontWeight="extrabold"
                color={useColorModeValue('gray.800', 'white')}
                letterSpacing="tight"
              >
                My Club
              </Text>
              <Text fontSize={{ base: 'sm', md: 'xs' }} color={useColorModeValue('gray.500', 'gray.400')} fontWeight="semibold" textTransform="uppercase" letterSpacing="wider">
                Admin Panel
              </Text>
            </VStack>
          </Flex>
        </Box>
        
        {renderNavItem({
          icon: FaHome, 
          to: "/",
          onClick: onClose,
          children: "Zpět na web"
        })}
        
        <Divider my={2} />
        
        {/* Dynamic Navigation */}
        {navLoading ? (
          <Flex justify="center" py={8}>
            <Spinner size="sm" />
          </Flex>
        ) : navItems.length > 0 ? (
          // Render dynamic navigation with collapsible categories
          <>
            {navItems.filter(item => {
              // Hide E-shop category if e-shop is disabled
              if (item.label === 'E-shop' && !isEshopEnabled) {
                return false;
              }
              return item.visible;
            }).map((item, index) => {
              const isCategory = item.type === 'dropdown';
              const hasChildren = Array.isArray(item.children) && item.children.length > 0;
              const catCollapsed = !!(item.id && collapsed[item.id]);
              const categoryHeader = (
                <Box px={2} py={2} onClick={() => toggleCollapsed(item.id)} cursor="pointer" role="button" aria-expanded={!catCollapsed}>
                  <Flex align="center" gap={2}>
                    <Box flex="1" height="1px" bg={dividerColor} />
                    <Text fontSize="xs" fontWeight="bold" textTransform="uppercase" letterSpacing="wider" color={categoryTextColor}>
                      {item.label}
                    </Text>
                    <Icon as={catCollapsed ? ChevronRightIcon : ChevronDownIcon} boxSize={3.5} color={categoryIconColor} />
                    <Box flex="1" height="1px" bg={dividerColor} />
                  </Flex>
                </Box>
              );

              if (isCategory) {
                return (
                  <Box>
                    {categoryHeader}
                    {hasChildren && (
                      <Collapse in={!catCollapsed} animateOpacity unmountOnExit>
                        <VStack align="stretch" spacing={1} px={1}>
                          {item.children!.filter(c => {
                            // Hide manual FACR if not in manual club data mode
                            if (c.page_type === 'manual_facr' && !isManualClubDataMode) {
                              return false;
                            }
                            return c.visible;
                          }).map((child, cidx) => {
                            const childIcon = getIconForPageType(child.page_type);
                            const childUrl = child.url || '#';
                            const showBadge = child.page_type === 'activities' && upcomingCount > 0;
                            return renderNavItem({
                              icon: childIcon,
                              to: childUrl,
                              onClick: onClose,
                              children: (
                                <Text as="span">
                                  {child.label}
                                  {showBadge && (
                                    <Text as="span" ml={2} fontSize="10px" px={2} py={0.5} borderRadius="full" bg={badgeBgGreen} color={badgeColorGreen} borderWidth="1px" borderColor={badgeBorderGreen}>
                                      {upcomingCount}
                                    </Text>
                                  )}
                                </Text>
                              )
                            });
                          })}
                        </VStack>
                      </Collapse>
                    )}
                  </Box>
                );
              }

              // Non-category top-level item
              // Hide manual FACR if not in manual club data mode
              if (item.page_type === 'manual_facr' && !isManualClubDataMode) {
                return null;
              }
              const itemIcon = getIconForPageType(item.page_type);
              const itemUrl = item.url || '#';
              const isActivities = item.page_type === 'activities';
              const showBadge = isActivities && upcomingCount > 0;
              return renderNavItem({
                icon: itemIcon,
                to: itemUrl,
                onClick: onClose,
                children: (
                  <Text as="span">
                    {item.label}
                    {showBadge && (
                      <Text as="span" ml={2} fontSize="10px" px={2} py={0.5} borderRadius="full" bg={badgeBgGreen} color={badgeColorGreen} borderWidth="1px" borderColor={badgeBorderGreen}>
                        {upcomingCount}
                      </Text>
                    )}
                  </Text>
                )
              });
            })}

            {/* Ensure Shortlinks is present even if not configured in dynamic nav (admins only) */}
            {isAdmin && !hasShortlinks && renderNavItem({
              icon: FaLink,
              to: "/admin/shortlinks",
              onClick: onClose,
              children: "Zkrácené odkazy"
            })}

            {/* Ensure Engagement page is present even if not configured in dynamic nav (admins only) */}
            {isAdmin && !hasEngagement && (
              <NavItem 
                icon={FaAward}
                to="/admin/engagement"
                onClick={onClose}
              >
                Odměny & Úspěchy
              </NavItem>
            )}

            {/* Ensure Comments moderation is present even if not configured in dynamic nav (admins only) */}
            {isAdmin && !hasComments && (
              <NavItem
                icon={FaComments}
                to="/admin/komentare"
                onClick={onClose}
              >
                Komentáře
              </NavItem>
            )}
            {/* Ensure Sweepstakes is present even if not configured in dynamic nav (admins only) */}
            {isAdmin && !hasSweepstakes && (
              <NavItem
                icon={FaGift}
                to="/admin/sweepstakes"
                onClick={onClose}
              >
                Soutěže
              </NavItem>
            )}
            {/* Ensure Competition Aliases is present even if not configured in dynamic nav (admins only) */}
            {isAdmin && !hasCompetitionAliases && (
              <NavItem
                icon={FaAward}
                to="/admin/aliasy-soutezi"
                onClick={onClose}
              >
                Alias soutěží
              </NavItem>
            )}
            
            {/* Ensure Clothing is present even if not configured in dynamic nav (admins only) */}
            {isAdmin && !hasClothing && (
              <NavItem
                icon={FaTshirt}
                to="/admin/obleceni"
                onClick={onClose}
              >
                Oblečení
              </NavItem>
            )}

            {/* Ensure E-shop products are present even if not configured in dynamic nav (admins only) - ONLY if e-shop is enabled */}
            {isAdmin && !hasEshopProducts && isEshopEnabled && (
              <NavItem
                icon={FaTshirt}
                to="/admin/eshop-produkty"
                onClick={onClose}
              >
                E‑shop produkty
              </NavItem>
            )}

            {/* Ensure About page (O klubu) and other core admin pages are present (admins only) */}
            {isAdmin && !hasAbout && (
              <NavItem
                icon={FaBook}
                to="/admin/o-klubu"
                onClick={onClose}
              >
                O klubu
              </NavItem>
            )}
            {isAdmin && !hasVideos && (
              <NavItem
                icon={FaVideo}
                to="/admin/videa"
                onClick={onClose}
              >
                Videa
              </NavItem>
            )}
            {isAdmin && !hasGallery && (
              <NavItem
                icon={FaImage}
                to="/admin/galerie"
                onClick={onClose}
              >
                Galerie (Zonerama)
              </NavItem>
            )}
            {isAdmin && !hasScoreboard && (
              <NavItem
                icon={FaTachometerAlt}
                to="/admin/scoreboard"
                onClick={onClose}
              >
                Tabule (Scoreboard)
              </NavItem>
            )}
            {isAdmin && !hasScoreboardRemote && (
              <NavItem
                icon={FaMobileAlt}
                to="/admin/scoreboard/remote"
                onClick={onClose}
              >
                Scoreboard Remote
              </NavItem>
            )}
            {isAdmin && !hasSponsors && (
              <NavItem
                icon={FaHandshake}
                to="/admin/sponzori"
                onClick={onClose}
              >
                Sponzoři
              </NavItem>
            )}
            {isAdmin && !hasBanners && (
              <NavItem
                icon={FaImage}
                to="/admin/bannery"
                onClick={onClose}
              >
                Bannery
              </NavItem>
            )}
            {isAdmin && !hasMessages && (
              <NavItem
                icon={FaEnvelope}
                to="/admin/zpravy"
                onClick={onClose}
              >
                Zprávy
              </NavItem>
            )}
            {isAdmin && !hasContacts && (
              <NavItem
                icon={FaAddressBook}
                to="/admin/kontakty"
                onClick={onClose}
              >
                Kontakty
              </NavItem>
            )}
            {isAdmin && !hasNewsletter && (
              <NavItem
                icon={FaPaperPlane}
                to="/admin/newsletter"
                onClick={onClose}
              >
                Zpravodaj
              </NavItem>
            )}
            {isAdmin && !hasPolls && (
              <NavItem
                icon={FaPoll}
                to="/admin/ankety"
                onClick={onClose}
              >
                Ankety
              </NavItem>
            )}
            {isAdmin && !hasAnalytics && (
              <NavItem 
                icon={FaChartBar} 
                to="/admin/analytika"
                onClick={onClose}
              >
                Analytika
              </NavItem>
            )}
            {isAdmin && !hasNavigation && (
              <NavItem 
                icon={FaBars} 
                to="/admin/navigace"
                onClick={onClose}
              >
                Navigace
              </NavItem>
            )}
            {isAdmin && !hasUsers && (
              <NavItem 
                icon={FaUsers} 
                to="/admin/uzivatele"
                onClick={onClose}
              >
                Uživatelé
              </NavItem>
            )}
            {isAdmin && !hasFiles && (
              <NavItem 
                icon={FaFolder} 
                to="/admin/soubory"
                onClick={onClose}
              >
                Soubory
              </NavItem>
            )}
            {isAdmin && !hasSettingsPage && (
              <NavItem 
                icon={FaPalette} 
                to="/admin/nastaveni"
                onClick={onClose}
              >
                Nastavení
              </NavItem>
            )}
            {isAdmin && !hasPrefetch && (
              <NavItem 
                icon={FaSyncAlt}
                to="/admin/prefetch"
                onClick={onClose}
              >
                Prefetch & Cache
              </NavItem>
            )}
            {isAdmin && !hasManualData && isManualClubDataMode && (
              <NavItem
                icon={FaFileAlt}
                to="/admin/manual-data"
                onClick={onClose}
              >
                Manuální data soutěží
              </NavItem>
            )}
          </>
        ) : (
          // Fallback to hardcoded navigation
          <>
            <Text fontSize="xs" fontWeight="bold" px={4} py={2} color={headerMutedColor} textTransform="uppercase" letterSpacing="wider">
              Hlavní
            </Text>
        
        {isAdmin && (
          <NavItem 
            icon={FaTachometerAlt} 
            to="/admin"
            onClick={onClose}
          >
            Nástěnka
          </NavItem>
        )}
        {isAdmin && isManualClubDataMode && (
          <NavItem 
            icon={FaFileAlt} 
            to="/admin/manual-data"
            onClick={onClose}
          >
            Manuální data soutěží
          </NavItem>
        )}
        
        {isAdmin && (
          <NavItem 
            icon={FaChartBar} 
            to="/admin/analytika"
            onClick={onClose}
          >
            Analytika
          </NavItem>
        )}
        
        <Text fontSize="xs" fontWeight="bold" px={4} py={2} color={headerMutedColor} textTransform="uppercase" letterSpacing="wider" mt={4}>
          Obsah
        </Text>
        {/* Core sports entities first */}
        {isAdmin && (
          <>
            <NavItem 
              icon={FaUsers} 
              to="/admin/tymy"
              onClick={onClose}
            >
              Týmy
            </NavItem>
            <NavItem 
              icon={FaCalendarAlt} 
              to="/admin/zapasy"
              onClick={onClose}
            >
              {/* Add subtle scroller hint */}
              <Text as="span">
                Zápasy
                <Text as="span" ml={2} fontSize="10px" px={2} py={0.5} borderRadius="full" bg={badgeBgGray} color={badgeColorGray} borderWidth="1px" borderColor={badgeBorderGray}>
                  scroller
                </Text>
              </Text>
            </NavItem>
          </>
        )}
        <NavItem 
          icon={FaCalendarAlt} 
          to="/admin/aktivity"
          onClick={onClose}
        >
          <Text as="span">
            Aktivity
            {upcomingCount > 0 && (
              <Text as="span" ml={2} fontSize="10px" px={2} py={0.5} borderRadius="full" bg={badgeBgGreen} color={badgeColorGreen} borderWidth="1px" borderColor={badgeBorderGreen}>
                {upcomingCount}
              </Text>
            )}
          </Text>
        </NavItem>
        {isAdmin && (
          <NavItem 
            icon={FaFutbol} 
            to="/admin/hraci"
            onClick={onClose}
          >
            Hráči
          </NavItem>
        )}
        {/* Other content */}
        <NavItem 
          icon={FaNewspaper} 
          to="/admin/clanky"
          onClick={onClose}
        >
          Články
        </NavItem>
        <NavItem 
          icon={FaLink} 
          to="/admin/shortlinks"
          onClick={onClose}
        >
          Zkrácené odkazy
        </NavItem>
        {isAdmin && (
          <>
            <NavItem 
              icon={FaBook} 
              to="/admin/o-klubu"
              onClick={onClose}
            >
              O klubu
            </NavItem>
            <NavItem 
              icon={FaImage} 
              to="/admin/videa"
              onClick={onClose}
            >
              Videa
            </NavItem>
            <NavItem 
              icon={FaImage} 
              to="/admin/galerie"
              onClick={onClose}
            >
              Galerie (Zonerama)
            </NavItem>
            <NavItem 
              icon={FaTachometerAlt} 
              to="/admin/scoreboard"
              onClick={onClose}
            >
              Tabule (Scoreboard)
            </NavItem>
            <NavItem 
              icon={FaMobileAlt} 
              to="/admin/scoreboard/remote"
              onClick={onClose}
            >
              Scoreboard Remote
            </NavItem>
            <NavItem 
              icon={FaPalette} 
              to="/admin/obleceni"
              onClick={onClose}
            >
              Oblečení
            </NavItem>
            <NavItem 
              icon={FaHandshake} 
              to="/admin/sponzori"
              onClick={onClose}
            >
              Sponzoři
            </NavItem>
            <NavItem 
              icon={FaImage} 
              to="/admin/bannery"
              onClick={onClose}
            >
              Bannery
            </NavItem>
            <NavItem 
              icon={FaEnvelope} 
              to="/admin/zpravy"
              onClick={onClose}
            >
              Zprávy
            </NavItem>
            <NavItem 
              icon={FaComments}
              to="/admin/komentare"
              onClick={onClose}
            >
              Komentáře
            </NavItem>
            <NavItem 
              icon={FaAddressBook} 
              to="/admin/kontakty"
              onClick={onClose}
            >
              Kontakty
            </NavItem>
            <NavItem 
              icon={FaPaperPlane} 
              to="/admin/newsletter"
              onClick={onClose}
            >
              Zpravodaj
            </NavItem>
            <NavItem 
              icon={FaPoll} 
              to="/admin/ankety"
              onClick={onClose}
            >
              Ankety
            </NavItem>
            <NavItem 
              icon={FaAward} 
              to="/admin/engagement"
              onClick={onClose}
            >
              Odměny & Úspěchy
            </NavItem>
          </>
        )}
        <Divider my={2} />
        
        {isAdmin && (
          <>
            <Text fontSize="xs" fontWeight="bold" px={4} py={2} color={headerMutedColor} textTransform="uppercase" letterSpacing="wider" mt={4}>
              Nastavení
            </Text>
            
            <NavItem 
              icon={FaBars} 
              to="/admin/navigace"
              onClick={onClose}
            >
              Navigace
            </NavItem>
            
            <NavItem 
              icon={FaAward} 
              to="/admin/aliasy-soutezi"
              onClick={onClose}
            >
              Alias soutěží
            </NavItem>

            <NavItem 
              icon={FaSyncAlt}
              to="/admin/prefetch"
              onClick={onClose}
            >
              Prefetch & Cache
            </NavItem>

            <NavItem 
              icon={FaUsers} 
              to="/admin/uzivatele"
              onClick={onClose}
            >
              Uživatelé
            </NavItem>
            <NavItem 
              icon={FaPalette} 
              to="/admin/nastaveni"
              onClick={onClose}
            >
              Nastavení
            </NavItem>
            <NavItem 
              icon={FaLink} 
              to="/admin/shortlinks"
              onClick={onClose}
            >
              Zkrácené odkazy
            </NavItem>
            <NavItem 
              icon={FaFolder} 
              to="/admin/soubory"
              onClick={onClose}
            >
              Soubory
            </NavItem>
          </>
        )}
        </>
        )}
        
        <Box mt="auto" mb={4} px={2}>
          <ChakraLink
            as="button"
            display="flex"
            alignItems="center"
            w="100%"
            px={4}
            py={2}
            borderRadius="md"
            _hover={{
              textDecoration: 'none',
              bg: useColorModeValue('red.50', 'red.900'),
              color: 'red.500',
            }}
            onClick={logout}
            color={useColorModeValue('red.500', 'red.300')}
          >
            <Icon as={FaSignOutAlt} mr={3} />
            <Text>Odhlásit se</Text>
          </ChakraLink>
        </Box>
      </VStack>
    </Box>
  );
};

export default AdminSidebar;
